/**
 * Vision mixer — the director's state machine (PVW/PGM bus, transitions,
 * instant replay, graphics on-air list, operator instructions).
 *
 * Pure TS. Time:
 *  - `opts.now()` returns the current SIM time; used for CutRecords / graphic timestamps.
 *  - `update(dt)` takes REAL seconds; advances transitions, replay playback and graphic auto-hide.
 *
 * Bus semantics follow a real switcher: TAKE/MIX swap PGM and PVW. The program
 * camera (and CutRecord) changes at the START of a transition; `transition`
 * exposes {from,to,progress} so the renderer can blend (mix) or swap the source
 * under the stinger at progress >= 0.5.
 */
import { Emitter } from '../core/events';
import type {
  CutRecord, DirectorState, GraphicInstance, GraphicKind, MatchEvent,
  OperatorInstruction, ReplayState, TransitionKind,
} from '../core/contracts';

export interface TransitionView {
  /** Source being transitioned away from (camId). */
  from: string;
  /** Destination (camId). */
  to: string;
  kind: TransitionKind;
  /** 0..1 */
  progress: number;
  durationS: number;
  /** True if `from` / `to` is the replay feed (camId is the replay angle). */
  fromReplay: boolean;
  toReplay: boolean;
}

/** Minimal view of the replay buffer the mixer needs (time range only). */
export interface ReplayRange { readonly size: number; readonly oldestT: number; readonly newestT: number }

export interface MixerOptions {
  /** Current sim time. */
  now: () => number;
  /** Event log, used when rollReplay(null) needs the latest replay-worthy event. */
  events?: () => readonly MatchEvent[];
  /** Default replay angle for an event (camId). Defaults to camIds[1] ?? camIds[0]. */
  pickReplayAngle?: (event: MatchEvent | null, camIds: readonly string[]) => string;
  initialProgram?: string;
  initialPreview?: string;
  mixDurationS?: number;        // default 0.5
  stingerDurationS?: number;    // default 0.6
  /** Seconds before the event start of the replay window (default 4). */
  replayPreS?: number;
  /** Seconds after the event (default 2.5). */
  replayPostS?: number;
  /** Fallback window when no replay-worthy event exists (default 6). */
  replayFallbackS?: number;
  /** Slow-mo speed around the key moment (default 0.5) and its half-width in sim seconds (default 1). */
  slowMoSpeed?: number;
  slowMoHalfWidthS?: number;
  /** Auto-hide durations (real seconds). 0 or Infinity = stays until hidden. */
  graphicDurations?: Partial<Record<GraphicKind, number>>;
  scorebugOn?: boolean;         // default true
}

export const DEFAULT_GRAPHIC_DURATIONS: Record<GraphicKind, number> = {
  scorebug: Infinity, clock: Infinity,
  goal_lower_third: 9, player_lower_third: 7, card_lower_third: 7, sub_lower_third: 7,
  team_lineup: 14, match_stats: 10, possession_bar: 8, player_stat_card: 9,
  // HT score auto-clears so a Q-shown panel can't sit over the second-half kick-off (CLUTTER).
  half_time_score: 10, full_time_score: Infinity, replay_bug: Infinity, var_check: 12,
};

/** Graphics sharing the main screen real-estate: showing one replaces the others. */
export const EXCLUSIVE_GRAPHICS: ReadonlySet<GraphicKind> = new Set<GraphicKind>([
  'goal_lower_third', 'player_lower_third', 'card_lower_third', 'sub_lower_third',
  'team_lineup', 'match_stats', 'player_stat_card', 'half_time_score', 'full_time_score',
]);

export type MixerEvents = {
  change: DirectorState;
  cut: CutRecord;
  transitionStart: TransitionView;
  transitionEnd: TransitionView;
  replayStart: ReplayState;
  replayEnd: ReplayState;
  graphicShown: GraphicInstance;
  graphicHidden: GraphicInstance;
  instruction: { camId: string; instruction: OperatorInstruction };
};

export interface Mixer {
  readonly state: DirectorState;
  readonly camIds: readonly string[];
  readonly transition: TransitionView | null;
  /** Sim time of the replayed event (slow-mo centre), null if none. */
  readonly replayKeyT: number | null;
  readonly emitter: Emitter<MixerEvents>;
  on<K extends keyof MixerEvents>(k: K, fn: (p: MixerEvents[K]) => void): () => void;

  hotPunch(camId: string): boolean;
  preview(camId: string): boolean;
  take(): boolean;
  mix(durationS?: number): boolean;

  rollReplay(event: MatchEvent | null, buffer: ReplayRange): boolean;
  replayCut(camId: string): boolean;
  endReplay(): boolean;
  updateReplay(dt: number): void;

  /** Advance transition + replay + graphic auto-hide by real dt. */
  update(dt: number): void;

  showGraphic(kind: GraphicKind, subjectId?: string): GraphicInstance;
  hideGraphic(target: number | GraphicKind): boolean;
  /** Hide if on air (any subject), else show. Returns true if now on air. */
  toggleGraphic(kind: GraphicKind, subjectId?: string): boolean;
  isOnAir(kind: GraphicKind): boolean;
  clearGraphics(): void;
  toggleScorebug(): boolean;

  setInstruction(camId: string, instruction: OperatorInstruction): void;
}

/** Replay playback speed at head time `headT` for a key moment at `keyT`. */
export function replaySpeedAt(headT: number, keyT: number | null, slowMo = 0.5, halfWidth = 1): number {
  if (keyT === null) return 1;
  return Math.abs(headT - keyT) <= halfWidth ? slowMo : 1;
}

/** Latest replay-worthy event (optionally only those after `sinceT`). */
export function latestReplayWorthy(events: readonly MatchEvent[], sinceT = -Infinity): MatchEvent | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (e.t < sinceT) break;
    if (e.ideal.replayWorthy) return e;
  }
  return null;
}

export function createMixer(camIds: string[], opts: MixerOptions): Mixer {
  if (!camIds.length) throw new Error('createMixer: need at least one camera');
  const now = opts.now;
  const mixDur = opts.mixDurationS ?? 0.5;
  const stingerDur = opts.stingerDurationS ?? 0.6;
  const pre = opts.replayPreS ?? 4;
  const post = opts.replayPostS ?? 2.5;
  const fallback = opts.replayFallbackS ?? 6;
  const slowMo = opts.slowMoSpeed ?? 0.5;
  const slowHalf = opts.slowMoHalfWidthS ?? 1;
  const durations: Record<GraphicKind, number> = { ...DEFAULT_GRAPHIC_DURATIONS, ...opts.graphicDurations };
  const pickAngle = opts.pickReplayAngle ?? ((_e, ids) => ids[1] ?? ids[0]!);
  const has = (id: string): boolean => camIds.includes(id);

  const emitter = new Emitter<MixerEvents>();
  const state: DirectorState = {
    programCamId: opts.initialProgram && has(opts.initialProgram) ? opts.initialProgram : camIds[0]!,
    previewCamId: opts.initialPreview && has(opts.initialPreview) ? opts.initialPreview : (camIds[1] ?? camIds[0]!),
    cuts: [],
    replay: { active: false, fromT: 0, toT: 0, headT: 0, speed: 1, camId: camIds[0]!, eventId: null },
    graphics: [],
    graphicsLog: [],
    instructions: {},
  };
  let transition: TransitionView | null = null;
  let replayKeyT: number | null = null;
  let nextGraphicId = 1;
  const graphicAge = new Map<number, number>();

  const changed = (): void => emitter.emit('change', state);

  const logCut = (camId: string, kind: TransitionKind, isReplay: boolean): void => {
    const rec: CutRecord = { t: now(), camId, transition: kind, isReplay };
    state.cuts.push(rec);
    emitter.emit('cut', rec);
  };

  const finishTransition = (): void => {
    if (!transition) return;
    const done = transition;
    done.progress = 1;
    transition = null;
    emitter.emit('transitionEnd', done);
  };

  const startTransition = (from: string, to: string, kind: TransitionKind, dur: number, fromReplay: boolean, toReplay: boolean): void => {
    finishTransition();
    if (kind === 'cut' || dur <= 0) return;
    transition = { from, to, kind, progress: 0, durationS: dur, fromReplay, toReplay };
    emitter.emit('transitionStart', transition);
  };

  /** Leave replay (if active) and put camId on PGM with the given transition. */
  const goLive = (camId: string, kind: TransitionKind, dur: number): void => {
    const wasReplay = state.replay.active;
    const from = wasReplay ? state.replay.camId : state.programCamId;
    if (wasReplay) {
      state.replay.active = false;
      replayKeyT = null;
    }
    state.programCamId = camId;
    logCut(camId, kind, false);
    startTransition(from, camId, kind, dur, wasReplay, false);
    if (wasReplay) emitter.emit('replayEnd', state.replay);
  };

  const hideInstance = (g: GraphicInstance): void => {
    const i = state.graphics.indexOf(g);
    if (i < 0) return;
    state.graphics.splice(i, 1);
    g.hiddenAt = now();
    graphicAge.delete(g.id);
    emitter.emit('graphicHidden', g);
  };

  const showInstance = (kind: GraphicKind, subjectId?: string): GraphicInstance => {
    const g: GraphicInstance = { id: nextGraphicId++, kind, shownAt: now() };
    if (subjectId !== undefined) g.subjectId = subjectId;
    state.graphics.push(g);
    state.graphicsLog.push(g);
    graphicAge.set(g.id, 0);
    emitter.emit('graphicShown', g);
    return g;
  };

  if (opts.scorebugOn ?? true) showInstance('scorebug');

  const mixer: Mixer = {
    state,
    camIds,
    get transition() { return transition; },
    get replayKeyT() { return replayKeyT; },
    emitter,
    on: (k, fn) => emitter.on(k, fn),

    hotPunch(camId) {
      if (!has(camId)) return false;
      if (!state.replay.active && state.programCamId === camId && !transition) return false;
      goLive(camId, 'cut', 0);
      changed();
      return true;
    },

    preview(camId) {
      if (!has(camId) || state.previewCamId === camId) return false;
      state.previewCamId = camId;
      changed();
      return true;
    },

    take() {
      const to = state.previewCamId;
      if (!state.replay.active && to === state.programCamId) return false;
      const old = state.programCamId;
      goLive(to, 'cut', 0);
      if (old !== to) state.previewCamId = old;
      changed();
      return true;
    },

    mix(durationS = mixDur) {
      const to = state.previewCamId;
      if (!state.replay.active && to === state.programCamId) return false;
      const old = state.programCamId;
      goLive(to, 'mix', durationS);
      if (old !== to) state.previewCamId = old;
      changed();
      return true;
    },

    rollReplay(event, buffer) {
      if (!buffer.size || !(buffer.newestT > buffer.oldestT)) return false;
      const ev = event ?? latestReplayWorthy(opts.events?.() ?? []);
      let fromT: number, toT: number;
      if (ev) {
        fromT = Math.max(buffer.oldestT, ev.t - pre);
        toT = Math.min(buffer.newestT, ev.t + post);
        if (toT - fromT < 0.5) return false; // event aged out of the buffer
      } else {
        toT = buffer.newestT;
        fromT = Math.max(buffer.oldestT, toT - fallback);
      }
      const angle = pickAngle(ev, camIds);
      const camId = has(angle) ? angle : camIds[0]!;
      const fromCam = state.replay.active ? state.replay.camId : state.programCamId;
      const fromReplay = state.replay.active;
      const r = state.replay;
      r.active = true; r.fromT = fromT; r.toT = toT; r.headT = fromT; r.camId = camId;
      r.eventId = ev ? ev.id : null;
      replayKeyT = ev ? ev.t : null;
      r.speed = replaySpeedAt(fromT, replayKeyT, slowMo, slowHalf);
      logCut(camId, 'stinger', true);
      startTransition(fromCam, camId, 'stinger', stingerDur, fromReplay, true);
      emitter.emit('replayStart', r);
      changed();
      return true;
    },

    replayCut(camId) {
      if (!state.replay.active || !has(camId) || camId === state.replay.camId) return false;
      const from = state.replay.camId;
      state.replay.camId = camId;
      logCut(camId, 'cut', true);
      startTransition(from, camId, 'cut', 0, true, true);
      changed();
      return true;
    },

    endReplay() {
      if (!state.replay.active) return false;
      goLive(state.programCamId, 'stinger', stingerDur);
      changed();
      return true;
    },

    updateReplay(dt) {
      const r = state.replay;
      if (!r.active || dt <= 0) return;
      r.speed = replaySpeedAt(r.headT, replayKeyT, slowMo, slowHalf);
      r.headT += dt * r.speed;
      if (r.headT >= r.toT) {
        r.headT = r.toT;
        mixer.endReplay();
      }
    },

    update(dt) {
      if (transition) {
        transition.progress = Math.min(1, transition.progress + dt / transition.durationS);
        if (transition.progress >= 1) finishTransition();
      }
      mixer.updateReplay(dt);
      let hid = false;
      for (let i = state.graphics.length - 1; i >= 0; i--) {
        const g = state.graphics[i]!;
        const age = (graphicAge.get(g.id) ?? 0) + dt;
        graphicAge.set(g.id, age);
        const d = durations[g.kind];
        if (d > 0 && Number.isFinite(d) && age >= d) { hideInstance(g); hid = true; }
      }
      if (hid) changed();
    },

    showGraphic(kind, subjectId) {
      const existing = state.graphics.find((g) => g.kind === kind && g.subjectId === subjectId);
      if (existing) { graphicAge.set(existing.id, 0); return existing; }
      for (let i = state.graphics.length - 1; i >= 0; i--) {
        const g = state.graphics[i]!;
        if (g.kind === kind || (EXCLUSIVE_GRAPHICS.has(kind) && EXCLUSIVE_GRAPHICS.has(g.kind))) hideInstance(g);
      }
      const g = showInstance(kind, subjectId);
      changed();
      return g;
    },

    hideGraphic(target) {
      const victims = state.graphics.filter((g) => (typeof target === 'number' ? g.id === target : g.kind === target));
      victims.forEach(hideInstance);
      if (victims.length) changed();
      return victims.length > 0;
    },

    toggleGraphic(kind, subjectId) {
      if (mixer.isOnAir(kind)) { mixer.hideGraphic(kind); return false; }
      mixer.showGraphic(kind, subjectId);
      return true;
    },

    isOnAir: (kind) => state.graphics.some((g) => g.kind === kind),

    clearGraphics() {
      const victims = state.graphics.filter((g) => g.kind !== 'scorebug' && g.kind !== 'clock');
      victims.forEach(hideInstance);
      if (victims.length) changed();
    },

    toggleScorebug() {
      if (mixer.isOnAir('scorebug')) { mixer.hideGraphic('scorebug'); return false; }
      showInstance('scorebug');
      changed();
      return true;
    },

    setInstruction(camId, instruction) {
      if (!has(camId)) return;
      state.instructions[camId] = instruction;
      emitter.emit('instruction', { camId, instruction });
      changed();
    },
  };
  return mixer;
}
